import { ContentSection, FooterContent, HeroHeader } from '@/types';
import { MessageCircleIcon, ShieldCheck } from 'lucide-react';

export const launchStatus: string = `Launching Soon`;
export const calendlyLink: string = `https://calendly.com/ben-heybtw`;
export const emailSection = {
    header: `Get Early Access`,
    inputPlaceholder: `Enter your email`,
    bulletPoints: [
        `Effortless Tracking`,
        `Smart Integrations`,
        `Intelligent Insights`,
    ],
    button: {
        text: `Request Your Demo`,
    },
};
export const heroHeader: HeroHeader = {
    header: `Hey, By The Way... Collaboration Shouldn't Be This Messy.`,
    subheader: `heyBTW eliminates co-marketing chaos—no more endless emails, messy threads, or spreadsheets. Just results for B2B Marketers.`,
    image: `/hero-img.webp`,
};
export const heroSteps: ContentSection = {
    subline: `How It Works`,
    header: `Transform Your Co-Marketing Collaboration`,
    subheader: ``,
    content: [
        {
            text: `Create Shared Workspaces`,
            subtext: `Set up dedicated collaboration spaces for each co-marketing event where nothing falls through the cracks and everyone on your GTM team has visibility on what they can do next.`,
            icon: 'userPlus',
        },
        {
            text: `Automate the Tedious Work`,
            subtext: `Jumpstart with playbook best practices. Let our AI handle scheduling, reminders, content coordination, and lead tracking so you can focus on creating a memorable experience.`,
            icon: 'sparkles',
        },
        {
            text: `Ecosystem Intelligence`,
            subtext: `Identify the right prospects and partners' existing customers to invite to your events, ensuring maximum impact and better-qualified leads. Putting you in the room with your next customer.`,
            icon: 'rocketLaunch',
        },
    ],
};

//side bar menus
export const ADMIN_SIDE_BAR = [
    {
        title: 'Test API',
        slug: 'admin',
        url: '/dashboard/admin',
        icon: ShieldCheck,
    },
];
export const EXPERIMENTAL_SIDE_BAR = [
    {
        title: 'Azure OpenAI Chat',
        slug: 'chat',
        url: '/dashboard/experimental/chat',
        icon: MessageCircleIcon,
    },
];

// TODO: Add Company Cards
export const featureCards: ContentSection = {
    header: `Powered by`,
    subheader: `What makes Next Landing possible`,
    content: [
        {
            text: `Next.js`,
            subtext: `The React Framework`,
            icon: 'nextjs',
        },
        {
            text: `shadcn/ui`,
            subtext: `Beautifully designed components`,
            icon: 'shadcnUi',
        },
        {
            text: `Vercel`,
            subtext: `Develop. Preview. Ship.`,
            icon: 'vercel',
        },
    ],
};

export const whyHeyBTW: ContentSection = {
    subline: `Why Choose Us`,
    header: `What You Get With heyBTW`,
    subheader: `Our platform is designed to help you manage your projects more effectively and increase productivity.`,
    content: [
        {
            text: `Ditch the Disorganization`,
            subtext: `Stop juggling Slack, spreadsheets, endless emails, and calendar invites just to run one partner event.`,
            icon: '',
        },
        {
            text: `Show the Results that Matter`,
            subtext: `Track where leads come from and prove the ROI of your co-marketing efforts.`,
            icon: '',
        },
        {
            text: `Save Tons of Time`,
            subtext: `Cut coordination work by 70% so you can focus on relationships, not admin tasks`,
            icon: '',
        },
    ],
};

export const demoContent: ContentSection = {
    subline: `Get Exclusive Early Access`,
    header: `Become a Co-Marketing Pioneer`,
    subheader: `Get exclusive early access to the platform that's redefining how partner marketing teams drive measurable pipeline growth.`,
    footer: `*Limited beta access - Join the waitlist today*`,
    button: {
        text: `Request Your Demo`,
        url: `mailto:hello@heybtw.com`,
    },
    email: {
        subject: `Request for heyBTW Demo`,
        body: `Hey BTW team,\n\nI wanted to reach out to learn more about how your platform can help our co-marketing efforts. Can we schedule a quick demo?\n\nThanks!`,
    },
};

export const footerContent: FooterContent = {
    logo: {
        text: 'heyBTW',
        tagline:
            "Oh, and BTW—we're transforming how B2B marketers collaborate.",
    },
    socialLinks: {
        twitter: 'https://twitter.com/heybtw',
        linkedin: 'https://www.linkedin.com/company/heybtw-ai/about/',
    },
    sections: {
        company: {
            title: 'Company',
            links: [
                {
                    text: 'About',
                    url: 'https://www.linkedin.com/company/heybtw-ai/about/',
                    icon: 'Info',
                },
                {
                    text: 'Contact',
                    url: 'mailto:hello@heybtw.com',
                    icon: 'Mail',
                    email: {
                        subject: `Request for heyBTW Demo`,
                        body: `Hey BTW team,\n\nI wanted to reach out to learn more about how your platform can help our co-marketing efforts. Can we schedule a quick demo?\n\nThanks!`,
                    },
                },
            ],
        },
        resources: {
            title: 'Resources',
            links: [
                {
                    text: 'Events',
                    url: 'https://lu.ma/heybtw',
                    icon: 'Calendar',
                    target: '_blank',
                },
            ],
        },
        legal: {
            title: 'Legal',
            links: [
                { text: 'Terms', url: '/terms', icon: 'Book' },
                { text: 'Privacy', url: '/privacy', icon: 'ShieldCheck' },
            ],
        },
    },
};

//forms
export const signInForm = {
    header: `Login`,
    subheader: `Enter your email and password below to sign in.`,
};

export const signUpForm = {
    header: `Sign Up`,
    subheader: `Enter your details below to create your account.`,
};

export const pagesTitles: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/dashboard/projects': 'Projects',
    '/dashboard/tasks': 'Tasks',
    '/dashboard/calendar': 'Calendar',
    '/dashboard/messages': 'Messages',
    '/dashboard/files': 'Files',
    '/dashboard/settings': 'Settings',
} as const;

export const workspaceModalSteps = {
    workspaceTitle: 'Create New Workspace',
    workspaceCompanyNameLabel: 'Organization Name',
    workspaceNonCompanyNameLabel: 'Name',
    workspaceIndustryLabel: 'Industry (optional)',
    workspaceCreateButton: 'Create Workspace',
    workspaceEmailDomainLabel: 'Email Domain',
    workspaceEmailDomainDescription:
        'Users with this email domain can join your workspace.',
    workspaceCancelButton: 'Cancel',
    workspaceSuccessMessage: 'Workspace created successfully',
    workspaceErrorMessage: 'Failed to create workspace',
    channelTitle: 'Create New Collaboration',
    invitePartnersTitle: 'Invite Partners',
    invitePartnersDescription:
        'Enter the partner domains for this event. Domain name with extension.' +
        'e.g. domain.com. Users with email addresses from these domains will be' +
        'automatically joined to the campaign.',
};

export const createChannelModalSteps = {
    channelTitle: 'Create New Collaboration',
    channelDescription: 'Create New Partner Collaboration',
    channelNameLabel: 'Name',
    channelNameDescription: 'Enter the name of the collaboration.',
    channelNamePlaceholder: 'Enter the name of the collaboration.',
    channelNameRequired: 'Name is required',
    channelNameMaxLength: 'Name must be less than 255 characters',
    channelNameMinLength: 'Name must be at least 3 characters',
    channelNameInvalid: 'Name is invalid',
    channelNameUnique: 'Name must be unique',
    channelCreateButton: 'Create Collaboration',
    channelCancelButton: 'Cancel',
    channelSuccessMessage: 'Collaboration created successfully',
    channelErrorMessage: 'Failed to create collaboration',
};

export const headerItems = {
    workspaceHeader: 'Workspace',
    channelHeader: 'Collaboration',
    adminHeader: 'Admin',
    experimentalHeader: 'Experimental',
};

export const channelSidebarMenu = {
    channelMenuItem: 'Collaborations',
    channelMenuButton: 'Add Collaboration',
    channelAdminItem: 'Admin',
    experimentalItem: 'Experimental',
    experimentalItemChat: 'Azure OpenAI Chat',
    partnerChannelsItem: 'Partner Collaborations',
};
